/**
 * Spillestedet sett fra huset selv (agent A): lesingene og skrivingene for salene, leien,
 * synligheten og kveldene.
 *
 * HVER SKRIVING PÅ EN SAL FEIER HELE `qk.venueHouse`: salen står i lista, i «Se som arrangør» og i
 * kveldenes salsnavn på en gang. Den feier også Spillesteder (`qk.venueMarket`), fordi huset ser sine
 * egne saler der også.
 */
import {
  activityListResponseSchema,
  venueNightDetailDtoSchema,
  venueNightListResponseSchema,
  venueNightSeriesDeletionDtoSchema,
  venueNightSeriesUpdateDtoSchema,
  venueNightShiftsCreatedDtoSchema,
  venueOverviewDtoSchema,
  venueRoomDtoSchema,
  venueRoomListResponseSchema,
  venueCalendarResponseSchema,
  venueRentPackageListSchema,
  type CopyVenueNightRequest,
  type CreateVenueCrewTemplateItemRequest,
  type CreateVenueNightRequest,
  type CreateVenueRoomFieldRequest,
  type CreateVenueRoomFileRequest,
  type CreateVenueRoomLayoutRequest,
  type CreateVenueRoomRequest,
  type UpdateVenueCrewTemplateItemRequest,
  type UpdateVenueNightRequest,
  type UpdateVenueRoomFieldRequest,
  type UpdateVenueRoomLayoutRequest,
  type SetVenueRoomPublishingRequest,
  type UpdateVenueRoomRequest,
  type VenueNightDetailDto,
  type VenueNightScope,
  type VenueNightStatus,
  type VenueNightSeriesDeletionDto,
  type VenueNightSeriesUpdateDto,
  type VenueNightShiftsCreatedDto,
  type VenueRoomDto,
  type CreateVenueRentPackageRequest,
  type UpdateVenueRentPackageRequest,
  type VenueCalendarResponse,
  type VenueRentPackageList,
} from '@tvitvi/shared';
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/api/client';
import { CACHE } from '@/api/cache-tiers';
import { qk } from '@/api/query-keys';

const ROOMS = '/venue-rooms';
const NIGHTS = '/venue-nights';

/** Siden kveldslista ber om. 50 om gangen; en sesong er flere sider. */
export const NIGHTS_PAGE_SIZE = 50;

export function venueRoomsQueryOptions(orgId: string, festivalId: string) {
  return queryOptions({
    queryKey: qk.venueHouseRooms(orgId, festivalId),
    queryFn: ({ signal }) =>
      apiRequest({
        path: ROOMS,
        query: { festivalId },
        schema: venueRoomListResponseSchema,
        signal,
      }),
    ...CACHE.detail,
  });
}

/** Arrangementene i en periode (sak 229): ingen årgang, bare datoer. Begge ender er valgfrie. */
export interface NightFilters {
  from?: string | undefined;
  to?: string | undefined;
  direction?: 'asc' | 'desc' | undefined;
  roomId?: string | undefined;
  status?: VenueNightStatus | undefined;
  /** Søket i lista (F35). */
  q?: string | undefined;
}

/** Siden kalenderen ber om: en måned har sjelden over hundre arrangementer, og den henter alle. */
export const NIGHTS_MONTH_PAGE_SIZE = 100;

export function venueNightsQueryOptions(
  orgId: string,
  festivalId: string,
  filters: NightFilters,
  pageSize: number = NIGHTS_PAGE_SIZE,
) {
  return infiniteQueryOptions({
    queryKey: qk.venueHouseNights(orgId, festivalId, { ...filters, pageSize }),
    queryFn: ({ signal, pageParam }) =>
      apiRequest({
        path: NIGHTS,
        query: {
          festivalId,
          limit: pageSize,
          sort: 'date',
          direction: filters.direction ?? 'asc',
          ...(filters.from === undefined ? {} : { from: filters.from }),
          ...(filters.to === undefined ? {} : { to: filters.to }),
          ...(filters.roomId === undefined ? {} : { roomId: filters.roomId }),
          ...(filters.status === undefined ? {} : { status: filters.status }),
          ...(filters.q === undefined || filters.q === '' ? {} : { q: filters.q }),
          ...(pageParam === null ? {} : { cursor: pageParam }),
        },
        schema: venueNightListResponseSchema,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
    ...CACHE.list,
  });
}

export function venueNightQueryOptions(orgId: string, nightId: string) {
  return queryOptions({
    queryKey: qk.venueHouseNight(orgId, nightId),
    queryFn: ({ signal }) =>
      apiRequest({ path: `${NIGHTS}/${nightId}`, schema: venueNightDetailDtoSchema, signal }),
    ...CACHE.detail,
  });
}

export function venueOverviewQueryOptions(orgId: string, festivalId: string) {
  return queryOptions({
    queryKey: qk.venueHouseOverview(orgId, festivalId),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `${NIGHTS}/overview`,
        query: { festivalId },
        schema: venueOverviewDtoSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * «SISTE AKTIVITET I HUSET» (sak 229): endringsloggen for tabellene et spillested lever i —
 * arrangementene, salene og datoforespørslene. Tabeller som også brukes av en festival (vakter,
 * oppgaver) er med vilje ute: et hus med begge ville fått festivalens endringer her.
 * `audit_log:list`, som resten av loggen.
 */
export const VENUE_ACTIVITY_TYPES = [
  'venue_nights',
  'venue_rooms',
  'venue_enquiries',
  'venue_room_layouts',
  'venue_room_fields',
  'venue_room_files',
  'venue_crew_template_items',
] as const;

export function venueActivityQueryOptions(orgId: string, limit = 8) {
  return queryOptions({
    queryKey: qk.typedActivityList(orgId, VENUE_ACTIVITY_TYPES, limit),
    queryFn: ({ signal }) =>
      apiRequest({
        path: '/activity',
        query: {
          sort: 'recent',
          direction: 'desc',
          limit,
          entityTypes: VENUE_ACTIVITY_TYPES.join(','),
        },
        schema: activityListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/* ------------------------------------------------------------------------------ skrivingene */

const roomInvalidates = (orgId: string) => [qk.venueHouse(orgId), qk.venueMarket(orgId)];

export function useCreateVenueRoom(
  orgId: string,
): UseMutationResult<VenueRoomDto, unknown, CreateVenueRoomRequest> {
  return useMutation({
    mutationFn: (body) =>
      apiRequest({ path: ROOMS, method: 'POST', body, schema: venueRoomDtoSchema }),
    meta: { invalidates: roomInvalidates(orgId) },
  });
}

export function useUpdateVenueRoom(
  orgId: string,
): UseMutationResult<VenueRoomDto, unknown, { roomId: string; body: UpdateVenueRoomRequest }> {
  return useMutation({
    mutationFn: ({ roomId, body }) =>
      apiRequest({ path: `${ROOMS}/${roomId}`, method: 'PATCH', body, schema: venueRoomDtoSchema }),
    meta: { invalidates: roomInvalidates(orgId) },
  });
}

/** Synligheten og den publiserte kalenderen (QA16 301.10): egen vei, egen celle (`venue:publish`). */
export function useSetVenueRoomPublishing(
  orgId: string,
): UseMutationResult<
  VenueRoomDto,
  unknown,
  { roomId: string; body: SetVenueRoomPublishingRequest }
> {
  return useMutation({
    mutationFn: ({ roomId, body }) =>
      apiRequest({
        path: `${ROOMS}/${roomId}/publishing`,
        method: 'PUT',
        body,
        schema: venueRoomDtoSchema,
      }),
    meta: { invalidates: roomInvalidates(orgId) },
  });
}

export function useDeleteVenueRoom(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (roomId) => apiRequest({ path: `${ROOMS}/${roomId}`, method: 'DELETE' }),
    meta: { invalidates: roomInvalidates(orgId) },
  });
}

export function useRestoreVenueRoom(
  orgId: string,
): UseMutationResult<VenueRoomDto, unknown, string> {
  return useMutation({
    mutationFn: (roomId) =>
      apiRequest({
        path: `${ROOMS}/${roomId}/restore`,
        method: 'POST',
        schema: venueRoomDtoSchema,
      }),
    meta: { invalidates: roomInvalidates(orgId) },
  });
}

/* ------------------------------------------------------- leiepakkene (QA15-S funn 6) */

export function venueRentPackagesQueryOptions(orgId: string, roomId: string) {
  return queryOptions({
    queryKey: qk.venueHouseRentPackages(orgId, roomId),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `${ROOMS}/${roomId}/rent-packages`,
        schema: venueRentPackageListSchema,
        signal,
      }),
    ...CACHE.detail,
  });
}

/** Én skriving på en pakke. Svaret er alltid hele lista for salen. */
export type RentPackageInput =
  | { roomId: string; method: 'POST'; body: CreateVenueRentPackageRequest }
  | { roomId: string; method: 'PATCH'; packageId: string; body: UpdateVenueRentPackageRequest }
  | { roomId: string; method: 'DELETE' | 'RESTORE'; packageId: string };

export function useRentPackage(
  orgId: string,
): UseMutationResult<VenueRentPackageList, unknown, RentPackageInput> {
  return useMutation({
    mutationFn: (input) => {
      const base = `${ROOMS}/${input.roomId}/rent-packages`;
      switch (input.method) {
        case 'POST':
          return apiRequest({
            path: base,
            method: 'POST',
            body: input.body,
            schema: venueRentPackageListSchema,
          });
        case 'PATCH':
          return apiRequest({
            path: `${base}/${input.packageId}`,
            method: 'PATCH',
            body: input.body,
            schema: venueRentPackageListSchema,
          });
        case 'DELETE':
          return apiRequest({
            path: `${base}/${input.packageId}`,
            method: 'DELETE',
            schema: venueRentPackageListSchema,
          });
        case 'RESTORE':
          return apiRequest({
            path: `${base}/${input.packageId}/restore`,
            method: 'POST',
            schema: venueRentPackageListSchema,
          });
      }
    },
    meta: { invalidates: [qk.venueHouse(orgId)] },
  });
}

/* ------------------------------------------------------- husets kalender (QA15-S B3) */

export interface VenueCalendarFilters {
  from: string;
  to: string;
  roomId?: string | undefined;
  q?: string | undefined;
}

export function venueCalendarQueryOptions(
  orgId: string,
  festivalId: string,
  filters: VenueCalendarFilters,
) {
  return queryOptions({
    queryKey: qk.venueHouseCalendar(orgId, { festivalId, ...filters }),
    queryFn: ({ signal }): Promise<VenueCalendarResponse> =>
      apiRequest({
        path: '/venue-calendar',
        query: {
          festivalId,
          from: filters.from,
          to: filters.to,
          ...(filters.roomId === undefined ? {} : { roomId: filters.roomId }),
          ...(filters.q === undefined || filters.q === '' ? {} : { q: filters.q }),
        },
        schema: venueCalendarResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * Delene av en sal: oppsett, egne felt, filer og mannskapsmal. Én mutasjon med en sti, fordi alle
 * svarer med hele salen og alle feier det samme.
 */
export type RoomPartInput =
  | { roomId: string; part: 'layouts'; method: 'POST'; body: CreateVenueRoomLayoutRequest }
  | {
      roomId: string;
      part: 'layouts';
      method: 'PATCH';
      partId: string;
      body: UpdateVenueRoomLayoutRequest;
    }
  | { roomId: string; part: 'fields'; method: 'POST'; body: CreateVenueRoomFieldRequest }
  | {
      roomId: string;
      part: 'fields';
      method: 'PATCH';
      partId: string;
      body: UpdateVenueRoomFieldRequest;
    }
  | { roomId: string; part: 'files'; method: 'POST'; body: CreateVenueRoomFileRequest }
  | { roomId: string; part: 'crew'; method: 'POST'; body: CreateVenueCrewTemplateItemRequest }
  | {
      roomId: string;
      part: 'crew';
      method: 'PATCH';
      partId: string;
      body: UpdateVenueCrewTemplateItemRequest;
    }
  | {
      roomId: string;
      part: 'layouts' | 'fields' | 'files' | 'crew';
      method: 'DELETE';
      partId: string;
    }
  | {
      roomId: string;
      part: 'layouts' | 'fields' | 'files' | 'crew';
      method: 'RESTORE';
      partId: string;
    }
  | { roomId: string; part: 'fields'; method: 'ORDER'; ids: string[] };

export function useRoomPart(
  orgId: string,
): UseMutationResult<VenueRoomDto, unknown, RoomPartInput> {
  return useMutation({
    mutationFn: (input) => {
      const base = `${ROOMS}/${input.roomId}/${input.part}`;
      switch (input.method) {
        case 'POST':
          return apiRequest({
            path: base,
            method: 'POST',
            body: input.body,
            schema: venueRoomDtoSchema,
          });
        case 'PATCH':
          return apiRequest({
            path: `${base}/${input.partId}`,
            method: 'PATCH',
            body: input.body,
            schema: venueRoomDtoSchema,
          });
        case 'DELETE':
          return apiRequest({
            path: `${base}/${input.partId}`,
            method: 'DELETE',
            schema: venueRoomDtoSchema,
          });
        case 'RESTORE':
          return apiRequest({
            path: `${base}/${input.partId}/restore`,
            method: 'POST',
            schema: venueRoomDtoSchema,
          });
        case 'ORDER':
          return apiRequest({
            path: `${base}/order`,
            method: 'PUT',
            body: { ids: input.ids },
            schema: venueRoomDtoSchema,
          });
      }
    },
    meta: { invalidates: roomInvalidates(orgId) },
  });
}

/* ----------------------------------------------------------------------------------- kvelder */

const nightInvalidates = (orgId: string) => [
  qk.venueHouse(orgId),
  qk.calendar(orgId),
  qk.shifts(orgId),
];

export function useCreateVenueNight(
  orgId: string,
): UseMutationResult<VenueNightDetailDto, unknown, CreateVenueNightRequest> {
  return useMutation({
    mutationFn: (body) =>
      apiRequest({ path: NIGHTS, method: 'POST', body, schema: venueNightDetailDtoSchema }),
    meta: { invalidates: nightInvalidates(orgId) },
  });
}

export function useUpdateVenueNight(
  orgId: string,
): UseMutationResult<
  VenueNightDetailDto,
  unknown,
  { nightId: string; body: UpdateVenueNightRequest }
> {
  return useMutation({
    mutationFn: ({ nightId, body }) =>
      apiRequest({
        path: `${NIGHTS}/${nightId}`,
        method: 'PATCH',
        body,
        schema: venueNightDetailDtoSchema,
      }),
    meta: { invalidates: nightInvalidates(orgId) },
  });
}

export function useDeleteVenueNight(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (nightId) => apiRequest({ path: `${NIGHTS}/${nightId}`, method: 'DELETE' }),
    meta: { invalidates: nightInvalidates(orgId) },
  });
}

export function useRestoreVenueNight(
  orgId: string,
): UseMutationResult<VenueNightDetailDto, unknown, string> {
  return useMutation({
    mutationFn: (nightId) =>
      apiRequest({
        path: `${NIGHTS}/${nightId}/restore`,
        method: 'POST',
        schema: venueNightDetailDtoSchema,
      }),
    meta: { invalidates: nightInvalidates(orgId) },
  });
}

export function useCreateNightShifts(
  orgId: string,
): UseMutationResult<VenueNightShiftsCreatedDto, unknown, string> {
  return useMutation({
    mutationFn: (nightId) =>
      apiRequest({
        path: `${NIGHTS}/${nightId}/shifts`,
        method: 'POST',
        schema: venueNightShiftsCreatedDtoSchema,
      }),
    meta: { invalidates: nightInvalidates(orgId) },
  });
}

/* ------------------------------------------------------- kopier og serier (F41, F49) */

export function useCopyVenueNight(
  orgId: string,
): UseMutationResult<
  VenueNightDetailDto,
  unknown,
  { nightId: string; body: CopyVenueNightRequest }
> {
  return useMutation({
    mutationFn: ({ nightId, body }) =>
      apiRequest({
        path: `${NIGHTS}/${nightId}/copy`,
        method: 'POST',
        body,
        schema: venueNightDetailDtoSchema,
      }),
    meta: { invalidates: nightInvalidates(orgId) },
  });
}

/** «Bare denne», «denne og senere» eller «alle» — samme endring på hvert av arrangementene. */
export function useUpdateVenueNightSeries(
  orgId: string,
): UseMutationResult<
  VenueNightSeriesUpdateDto,
  unknown,
  { nightId: string; scope: VenueNightScope; body: UpdateVenueNightRequest }
> {
  return useMutation({
    mutationFn: ({ nightId, scope, body }) =>
      apiRequest({
        path: `${NIGHTS}/${nightId}/series`,
        method: 'PATCH',
        query: { scope },
        body,
        schema: venueNightSeriesUpdateDtoSchema,
      }),
    meta: { invalidates: nightInvalidates(orgId) },
  });
}

export function useDeleteVenueNightSeries(
  orgId: string,
): UseMutationResult<
  VenueNightSeriesDeletionDto,
  unknown,
  { nightId: string; scope: VenueNightScope }
> {
  return useMutation({
    mutationFn: ({ nightId, scope }) =>
      apiRequest({
        path: `${NIGHTS}/${nightId}/series`,
        method: 'DELETE',
        query: { scope },
        schema: venueNightSeriesDeletionDtoSchema,
      }),
    meta: { invalidates: nightInvalidates(orgId) },
  });
}

export function useRestoreVenueNights(
  orgId: string,
): UseMutationResult<VenueNightSeriesDeletionDto, unknown, readonly string[]> {
  return useMutation({
    mutationFn: (ids) =>
      apiRequest({
        path: `${NIGHTS}/restore`,
        method: 'POST',
        body: { ids },
        schema: venueNightSeriesDeletionDtoSchema,
      }),
    meta: { invalidates: nightInvalidates(orgId) },
  });
}
